import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router';

import { useAuth } from '../auth/AuthContext';
import { hasAtLeast, type Role } from '../auth/roles';
import { canSeeCabinet, canSeeGroups, curatorNavOrder, isCuratorRole } from '../auth/session-provider';
import { PdnToggle } from '../demo/PdnToggle';
import { ThemeToggle } from './ThemeToggle';
import { UserChip } from './UserChip';

interface NavItem {
  to: string;
  label: string;
  icon: ReactElement;
  /** Test hook (`nav-<id>`); also the stable key for e2e assertions. */
  id: string;
  /** `end` stops a prefix route (notably `/`) from matching every URL. */
  end?: boolean;
  /** Minimum role the link is offered to. Absent = every role. */
  minRole?: Role;
  /** Offered only when `canSeeCabinet` says the session may use the cabinet. */
  cabinetOnly?: boolean;
  /** ADDITIVE (v1.9.5, T.Z52) — offered to a КУРАТОР-role session as well (scoped
   * server-side to its own venues). Only «Педагоги» carries it today. */
  curatorVisible?: boolean;
  /** ADDITIVE (T.Z60) — offered to a КУРАТОР-role session ONLY (`canSeeGroups`):
   * a curator surface the staff nav never shows. Only «Группы» carries it. */
  groupsOnly?: boolean;
  /** Exactly ADMIN: `minRole: 'ADMIN'` opens for SUPPORT as well (it ranks above ADMIN, T.Z155).
   * Only «Дашборд для кураторов» carries it, for its check stage (DATAANALYTICS-112). */
  adminOnly?: boolean;
  /** Offered to a КУРАТОР-role session ONLY, like `groupsOnly` but with no link condition: the api decides what the
   * page holds. Only the curator's «Дашборд» carries it (DATAANALYTICS-112, owner 03.11). */
  curatorOnly?: boolean;
}

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconDashboard() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M4 4h7v6H4zM13 4h7v4h-7zM4 13h7v7H4zM13 11h7v9h-7z" {...ICON_STROKE} />
    </svg>
  );
}

function IconApplications() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M6 3.5h9.5L19 7v13.5H6z" {...ICON_STROKE} />
      <path d="M9 10h7M9 13.5h7M9 17h4" {...ICON_STROKE} />
    </svg>
  );
}

function IconVenues() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M4 20.5V6l8-3v17.5M12 20.5h8V10l-8-2.5" {...ICON_STROKE} />
      <path d="M7 9h2M7 13h2M7 17h2M15 12h2M15 16h2" {...ICON_STROKE} />
    </svg>
  );
}

function IconQuotas() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M4 20h16" {...ICON_STROKE} />
      <path d="M7 20v-6M12 20V7M17 20v-9" {...ICON_STROKE} />
    </svg>
  );
}

/** A database-cylinder glyph — «Данные УНТИ» (T.U2) is the raw mirror read. */
function IconUntiData() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <ellipse cx="12" cy="5.5" rx="7" ry="2.8" {...ICON_STROKE} />
      <path d="M5 5.5v13c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8v-13" {...ICON_STROKE} />
      <path d="M5 12c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8" {...ICON_STROKE} />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="3" {...ICON_STROKE} />
      <path
        d="M12 3.5v2.2M12 18.3v2.2M4.9 7.8l1.9 1.1M17.2 15.1l1.9 1.1M4.9 16.2l1.9-1.1M17.2 8.9l1.9-1.1"
        {...ICON_STROKE}
      />
    </svg>
  );
}

/** A person-with-lanyard glyph — the teacher registry (T.F6) is the sixth screen. */
function IconTeachers() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="7" r="3.2" {...ICON_STROKE} />
      <path d="M5.5 19.5c.7-3.4 3.3-5.4 6.5-5.4s5.8 2 6.5 5.4" {...ICON_STROKE} />
    </svg>
  );
}

/** A heat-grid glyph — «Распределение по ЦА» (T.W1) is a venue × programme × format matrix. */
function IconDistribution() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="15" rx="1.5" {...ICON_STROKE} />
      <path d="M9 4.5v15M15 4.5v15M3.5 12h17" {...ICON_STROKE} />
    </svg>
  );
}

/**
 * The ten product screens (T.C2–T.C5, + teachers T.F6, + the ЦА pivot T.W1,
 * + the curator cabinet T.Y8, + the mailings surface T.Z124, + the raw mirror
 * «Данные УНТИ» T.U2).
 *
 * T.X2 (owner 2026-09-11): the admin screens — «Педагоги», «Квоты»,
 * «Настройки», joined by «Данные УНТИ» (T.U2, below «Квоты» per the owner
 * brief) — are offered to the ADMIN only; a manager works from «Дашборд»,
 * «Заявления», «Площадки» and «Распределение ЦА». The router hides the same
 * routes (`minRole`), so a hand-typed URL meets the 403 state rather than the page.
 *
 * WAVE 10 (D21) — the nav mirrors the api's role+link boundary, not the provider:
 *
 *   * a КУРАТОР-role session (the `VIEWER` enum value) sees «Кабинет» — the staff
 *     items are gone, because the api refuses them through EITHER provider.
 *     ADDITIVE (v1.9.5, T.Z52, owner request): «Педагоги» joins the curator's list,
 *     because the api now serves that ONE read through the curator's venue scope
 *     (`ExternalAccess('scoped')` on #7.5) — every other staff item is still refused.
 *     ADDITIVE (T.Z60, owner 2026-09-15): «Группы» joins too — its OWN page, offered
 *     to the curator only — and the curator's three items are ordered «Кабинет»,
 *     «Педагоги», «Группы» (`curatorNavOrder`); «Педагоги» reads directly below
 *     «Кабинет» for the curator, while the staff list keeps the staff order.
 *   * a МЕНЕДЖЕР keeps the staff screens, and «Кабинет» appears only when the api's
 *     effective cabinet scope is non-empty (the prefill-capable manager, M1);
 *   * an ADMIN keeps the staff + admin tier and — A6 (owner UX pass, 2026-09-12) —
 *     is NOT offered «Кабинет» anymore;
 *   * the API/DB/Redis health chips are hidden for EVERYONE (A7, overriding T.Y18's
 *     curator-only rule): they are dev comfort for the people who run the system,
 *     and that job belongs to the ops surfaces, not to this sidebar.
 */
const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', to: '/', label: 'Дашборд', icon: <IconDashboard />, end: true },
  { id: 'curator-dashboard', to: '/curator-dashboard', label: 'Дашборд для кураторов', icon: <IconDashboard />, adminOnly: true },
  { id: 'applications', to: '/applications', label: 'Заявления', icon: <IconApplications /> },
  { id: 'venues', to: '/venues', label: 'Площадки', icon: <IconVenues /> },
  { id: 'teachers', to: '/teachers', label: 'Педагоги', icon: <IconTeachers />, minRole: 'ADMIN', curatorVisible: true },
  { id: 'ca-distribution', to: '/ca-distribution', label: 'Распределение ЦА', icon: <IconDistribution /> },
  { id: 'cabinet', to: '/cabinet', label: 'Кабинет', icon: <IconCabinet />, cabinetOnly: true },
  // T.Z60 — «Группы» is the curator's own page (the section moved out of the
  // cabinet); the item is offered through `canSeeGroups`, i.e. to a КУРАТОР-role
  // session only, and its PLACE in the curator's list comes from `curatorNavOrder`.
  { id: 'groups', to: '/groups', label: 'Группы', icon: <IconGroups />, groupsOnly: true },
  // DATAANALYTICS-112 (owner 03.11) — the curator's own dashboard, directly below «Кабинет» (`curatorNavOrder`).
  { id: 'my-dashboard', to: '/my-dashboard', label: 'Дашборд', icon: <IconDashboard />, curatorOnly: true },
  { id: 'quotas', to: '/quotas', label: 'Квоты', icon: <IconQuotas />, minRole: 'ADMIN' },
  // T.U2 — «Данные УНТИ» sits directly BELOW «Квоты» (owner spec) and is the
  // fourth admin screen: the mirror carries pgu_id (person-identifying) and is
  // the raw upstream truth, so the item is offered to the ADMIN only and the
  // router hides the route the same way (`minRole="ADMIN"`); #36/#37 answer an
  // ADMIN session only — the api is the boundary, as everywhere.
  { id: 'unti', to: '/unti', label: 'Данные УНТИ', icon: <IconUntiData />, minRole: 'ADMIN' },
  // T.Z124 — «Рассылки» sits BELOW «Квоты» (owner spec) and is a staff item:
  // MANAGER+ sees it, a КУРАТОР-role session never does (the api refuses the
  // whole /mailings surface to a curator anyway — the fifth guard).
  { id: 'mailings', to: '/mailings', label: 'Рассылки', icon: <IconMailings />, minRole: 'MANAGER' },
  { id: 'settings', to: '/settings', label: 'Настройки', icon: <IconSettings />, minRole: 'ADMIN' },
];

/** An envelope glyph — the «Рассылки» staff surface (T.Z124). */
function IconMailings() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" {...ICON_STROKE} />
      <path d="M4 7l8 6 8-6" {...ICON_STROKE} />
    </svg>
  );
}

/** A person-at-a-desk glyph — the curator cabinet (T.Y8) is the eighth screen. */
function IconCabinet() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="12" rx="1.5" {...ICON_STROKE} />
      <path d="M7 20.5h10M12 16.5v4M7 8.5h5M7 11.5h7" {...ICON_STROKE} />
    </svg>
  );
}

/** A three-heads glyph — the study groups of a venue (T.Z50, own page since T.Z60). */
function IconGroups() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="7.5" cy="8.5" r="2.6" {...ICON_STROKE} />
      <circle cx="16.5" cy="8.5" r="2.6" {...ICON_STROKE} />
      <path d="M3 19c.6-3 2.3-4.6 4.5-4.6S11.4 16 12 19M12.6 19c.6-3 2.3-4.6 4.5-4.6S20.4 16 21 19" {...ICON_STROKE} />
    </svg>
  );
}

/**
 * The app shell: a sidebar (brand, nav, health chips) + a topbar (page title,
 * theme toggle, user chip) around the routed page.
 */
export function AppLayout() {
  const { role, cabinetVenueIds } = useAuth();
  // WAVE 10 (D21): a curator-role session is cabinet-only — the whole staff list
  // collapses to the one item, whichever way they signed in. A МЕНЕДЖЕР is offered
  // «Кабинет» through the same check when their effective cabinet scope is non-empty
  // (the prefill-capable manager), and an ADMIN is not offered it at all (A6).
  const curatorOnly = isCuratorRole(role);
  // T.Z60 — the two lists read the SAME items through different rules: the curator's
  // is «Кабинет», «Педагоги», «Группы» (in that order, `curatorNavOrder`), the
  // staff's keeps the array order above untouched.
  const items = curatorOnly
    ? curatorNavOrder(
        NAV_ITEMS.filter(
          (item) =>
            item.cabinetOnly === true ||
            item.curatorVisible === true ||
            item.curatorOnly === true ||
            (item.groupsOnly === true && canSeeGroups({ role, cabinetVenueIds })),
        ),
      )
    : NAV_ITEMS.filter((item) => {
        if (item.minRole !== undefined && !hasAtLeast(role, item.minRole)) return false;
        if (item.adminOnly === true && role !== 'ADMIN') return false;
        if (item.curatorOnly === true) return false;
        if (item.cabinetOnly === true && !canSeeCabinet({ role, cabinetVenueIds })) return false;
        if (item.groupsOnly === true && !canSeeGroups({ role, cabinetVenueIds })) return false;
        return true;
      });

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="sidebar__logo" aria-hidden="true">
            CB
          </span>
          <span className="sidebar__title">CB Manager</span>
        </div>
        <nav className="sidebar__nav" aria-label="Основная навигация" data-testid="nav">
          {items.map(
            ({ to, label, icon, end, id }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
                }
                data-testid={`nav-${id}`}
              >
                <span className="sidebar__icon">{icon}</span>
                <span className="sidebar__label">{label}</span>
              </NavLink>
            ),
          )}
        </nav>
        {/* A7 (owner UX pass, 2026-09-12): the health chips are gone for EVERYONE —
            dev/ops comfort, not product navigation. The footer keeps the spacing. */}
        <div className="sidebar__footer" />
      </aside>

      <div className="shell__main">
        <header className="topbar">
          <div className="topbar__spacer" />
          <ThemeToggle />
          {/* T.Z112 — the personal-data mask switch (icon-only since T.Z118),
              next to the theme toggle; the engine lives in ../demo/demo-mask. */}
          <PdnToggle />
          <UserChip />
        </header>
        <main className="page">
          <Outlet />
        </main>
      </div>
      {/* T.Z49 — the UseDesk support chat (all roles, owner 2026-09-14) used to be
          rendered here, inside the shell, which is what kept it off /login and
          /logged-out. T.Z116 moved it to the router's pathless root route: it now
          covers those pre-auth screens too (anonymously), which is the point —
          a visitor who cannot sign in is exactly the visitor who needs support. */}
    </div>
  );
}
